import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useNavMode } from "@/contexts";
import { useRole } from "@/hooks/auth";
import { Roles } from "@/types/auth/auth";
import HelpGenieLogo from "../ui/HelpGenieLogo";
import { GenieSwitcher } from "@/components/suite/GenieSwitcher";
import SidebarNav from "./SidebarNav";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const MASCOT_SRC = "/genie-mascots/role/support.png";

interface SidebarProps {
  isMobile: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  hasEverExpanded: boolean;
  onOpenHelpGenie: () => void;
}

const Sidebar = ({ isMobile, sidebarOpen, setSidebarOpen, hasEverExpanded, onOpenHelpGenie }: SidebarProps) => {
  const { signOut, userProfile } = useAuth();
  const { hasRole } = useRole();
  const { mode, setMode } = useNavMode();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate("/signin");
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      {isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        ></div>
      )}
      {/* Wrapper exists so the "More options" nudge can sit just outside the rail's
          right edge — the rail itself is overflow-hidden for the collapse animation. */}
      <div className="relative shrink-0">
      <div
        className={`bg-gradient-to-b from-[#F9FAFC] to-white flex flex-col overflow-hidden ${
          isMobile
            ? `fixed top-0 left-0 h-screen-safe z-40 transform transition-transform duration-300 ease-in-out w-72 border-r border-genie-border shadow-sm ${
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
              }`
            : `sticky top-0 h-screen-safe transition-all duration-200 ${
                sidebarOpen ? "md:w-72 border-r border-genie-border shadow-sm" : "md:w-16"
              }`
        }`}
      >
        <div className={sidebarOpen ? "px-4 pt-4" : "pt-3 flex flex-col items-center"}>
          <div className={`flex items-center ${sidebarOpen ? "justify-between w-full" : "flex-col"}`}>
            <div className={`flex items-center min-w-0 ${sidebarOpen ? "" : "p-3"}`}>
              <HelpGenieLogo size={sidebarOpen ? 36 : 28} />
              {sidebarOpen && (
                // text-2xl (was 3xl): the header row now also carries the GenieSwitcher —
                // at 3xl the wordmark runs under it. Matches the approved mock's proportions.
                <span className="ml-3 text-2xl font-bold tracking-tight text-genie-logo whitespace-nowrap">Help Genie</span>
              )}
            </div>
            {sidebarOpen ? (
              <div className="flex shrink-0 items-center gap-1">
                <GenieSwitcher />
                <button
                  className="rounded-full p-1.5 hover:bg-genie-primary/10 hover:text-genie-primary transition-all duration-200"
                  aria-label={isMobile ? "Close navigation menu" : "Collapse sidebar"}
                  onClick={() => setSidebarOpen(false)}
                  type="button"
                >
                  <ChevronLeft size={20} />
                </button>
              </div>
            ) : (
              !isMobile && (
                <button
                  className="rounded-full p-1.5 hover:bg-genie-primary/10 hover:text-genie-primary transition-all duration-200 mt-1"
                  aria-label="Expand sidebar"
                  onClick={() => setSidebarOpen(true)}
                  type="button"
                >
                  <ChevronRight size={20} />
                </button>
              )
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <SidebarNav sidebarOpen={sidebarOpen} />
        </div>

        {/* Bottom chrome — hidden when collapsed so the rail stays bare and immersive */}
        {sidebarOpen && (
          <div className="border-t border-genie-border/50 p-4 space-y-3">
            {/* Your Genie trigger */}
            <button
              onClick={onOpenHelpGenie}
              className="group w-full rounded-xl transition-all duration-200 overflow-hidden"
              style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.12) 0%, rgba(255,77,184,0.12) 100%)" }}
            >
              <div className="flex items-center gap-3 px-3 py-2.5">
                <img
                  src={MASCOT_SRC}
                  alt="Your Genie"
                  className="h-12 w-12 rounded-xl object-cover ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all shadow-sm"
                />
                <div className="flex flex-col items-start min-w-0">
                  <span className="font-bold text-sm bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                    Your Genie
                  </span>
                  <span className="text-xs text-gray-400">Ask me anything</span>
                </div>
              </div>
            </button>

            {/* User Profile Section */}
            <Link
              to="/genie-hub/account-settings"
              className="w-full flex items-center rounded-lg transition-all duration-200 px-3 py-2 gap-3"
              style={{ background: "linear-gradient(90deg, rgba(78,156,255,0.08) 0%, rgba(255,77,184,0.08) 100%)" }}
            >
              <Avatar className="h-10 w-10 flex-shrink-0 border-2 border-white shadow-sm">
                {userProfile?.avatar_url ? (
                  <AvatarImage src={userProfile.avatar_url} alt={userProfile.full_name || "User"} />
                ) : null}
                <AvatarFallback className="font-bold text-white" style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)" }}>
                  {getInitials(userProfile?.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-sm font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] truncate">
                  {userProfile?.full_name || "User"}
                </span>
                <span className="text-xs text-genie-textSecondary capitalize truncate">
                  {userProfile?.role?.replace("_", " ") || "User"}
                </span>
              </div>
            </Link>

            {/* Admin toggle */}
            {hasRole([Roles.Admin]) && (
              <div className="space-y-2">
                {mode === "business" ? (
                  <Link
                    to="/admin"
                    onClick={() => setMode("admin")}
                    className="w-full flex items-center rounded-lg transition-all duration-200 px-3 py-2 text-genie-primary hover:bg-[#F9FAFC] font-medium"
                  >
                    <GenieIcon name="settings" size={20} className="h-5 w-5 flex-shrink-0" />
                    <span className="ml-2 font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                      Admin Operations
                    </span>
                  </Link>
                ) : (
                  <button
                    onClick={() => { setMode("business"); navigate("/dashboard"); }}
                    className="w-full flex items-center rounded-lg transition-all duration-200 px-3 py-2 text-genie-primary hover:bg-[#F9FAFC] font-medium"
                  >
                    <GenieIcon name="dashboard" size={20} className="h-5 w-5 flex-shrink-0" />
                    <span className="ml-2 font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
                      Back to Business
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center text-[#D6409F] hover:text-[#B8368A] hover:bg-[#D6409F]/10 rounded-lg transition-all duration-200 px-3 py-2 justify-start"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              <span className="ml-2">Logout</span>
            </button>
          </div>
        )}
      </div>

      {/* One-time discoverability nudge — points the eye back at the pop-out button.
          Shows on the collapsed rail (any page) until the user expands once, ever. */}
      {!isMobile && !sidebarOpen && !hasEverExpanded && (
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Expand navigation for more options"
          className="absolute left-full top-[66px] z-50 ml-2 flex items-center rounded-full px-3 py-1.5 text-xs font-semibold text-white whitespace-nowrap shadow-lg shadow-genie-primary/30 animate-nudge-x transition-all duration-200 hover:scale-105 hover:shadow-xl"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        >
          {/* notch pointing back toward the rail; sits on the blue end of the gradient */}
          <span className="absolute -left-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 rotate-45 rounded-[1px] bg-[#4E9CFF]" />
          More options
        </button>
      )}
      </div>
    </>
  );
};

export default Sidebar;
